import {
  Button,
  Container,
  DatePicker,
  ExpandableSection,
  FileUpload,
  FormField,
  Header,
  Input,
  ProgressBar,
  Select,
  SpaceBetween,
  Toggle,
} from "@cloudscape-design/components";
import { useUserProfile } from "../../../components";
import { useTranslation } from "react-i18next";
import { gtmMaterialsCategoryOptions } from "../../../common/formOptions";
import { addForm, removeForm, updateForm, moveForm } from "../../../common/utils";
import { v4 as uuidv4 } from "uuid";
import paletteConfig from "../../../config/aws-palette-config.json";

const DEBUG = paletteConfig.debugMode;

function FileUploaderFooter({ formattedData, gtmFiles, setGtmFiles, setSubmitDisabled, item }) {
  const { t } = useTranslation();
  const { contentLanguage } = useUserProfile();

  /*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
  function isFileValid(fileSelected) {
    const file = fileSelected[0];
    if (!file) {
      DEBUG && console.log("The file hasn't been selected.");
      setSubmitDisabled(false);
      return "valid";
    }

    // 检查文件类型是否符合条件
    const acceptedFileTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/bmp",
      "image/webp",
      "image/svg+xml",
      "video/mp4",
    ];
    const isAcceptedFileType = acceptedFileTypes.includes(file.type);

    // 检查文件大小是否小于 100MB
    const isSizeValid = file.size <= 100 * 1024 * 1024;

    let validationResult;

    if (isAcceptedFileType && isSizeValid) {
      DEBUG && console.log("文件类型和大小均有效");
      setSubmitDisabled(false);
      validationResult = "valid";
    } else {
      if (!isAcceptedFileType && !isSizeValid) {
        DEBUG && console.log("文件类型无效，请上传文档或图片文件；文件大小超过(100MB)，请上传较小的文件");
        validationResult = "typeAndSizeError";
      } else if (!isAcceptedFileType) {
        DEBUG && console.log("文件类型无效，请上传文档或图片文件");
        validationResult = "typeError";
      } else if (!isSizeValid) {
        DEBUG && console.log("文件大小超过(100MB)，请上传较小的文件");
        validationResult = "sizeError";
      }
      setSubmitDisabled(true);
    }

    return validationResult;
  }

  /*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
  const onChangeFileHandler = (newFileValue, itemId) => {
    // 检查文件是否有效
    const validationResult = isFileValid(newFileValue);
    const fileIsValid = validationResult === "valid";

    let updatedErrorText = "";
    if (validationResult === "typeAndSizeError") {
      updatedErrorText = t("FileUpload.fileTypeErrorAndSizeError");
    } else if (validationResult === "typeError") {
      updatedErrorText = t("FileUpload.fileTypeError");
    } else if (validationResult === "sizeError") {
      updatedErrorText = t("FileUpload.fileSizeError");
    }
    setGtmFiles((prevGtmFiles) => {
      // 检查是否存在具有匹配 id 的对象
      const existingFileObj = prevGtmFiles.find((fileObj) => fileObj.id === itemId);

      // 如果存在，则更新该对象
      if (existingFileObj) {
        const updatedGtmFiles = prevGtmFiles.map((fileObj) => {
          if (fileObj.id === itemId) {
            return { ...fileObj, file: fileIsValid ? newFileValue : fileObj.file, errorText: updatedErrorText };
          }
          return fileObj;
        });
        return updatedGtmFiles;
      } else {
        // 如果不存在，且文件有效，创建一个新对象并将其添加到 prevGtmFiles 中
        const newFileObj = { id: itemId, file: fileIsValid ? newFileValue : [], errorText: updatedErrorText, progress: 0, complete: false };
        return [...prevGtmFiles, newFileObj];
      }
    });
  };

  const theCurrentFileKey = formattedData["gtmMaterials_" + contentLanguage].find((Obj) => Obj.id === item.id).fileKey;
  const uploadButtonText = theCurrentFileKey ? theCurrentFileKey : t("solutions:SolutionForm.fileText");

  /*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
  return (
    <ExpandableSection headerText={t("FormButton.additional_settings_uploadfiles")} variant="footer">
      <SpaceBetween size="l">
        <FormField label={t(`solutions:SolutionForm.fileLabel`)} description={t(`solutions:SolutionForm.fileLabel_Des`)}>
          <FileUpload
            value={gtmFiles.find((Obj) => Obj.id === item.id)?.file || []}
            onChange={({ detail }) => {
              onChangeFileHandler(detail.value, item.id);
            }}
            errorText={gtmFiles.find((Obj) => Obj.id === item.id)?.errorText || ""}
            constraintText={t(`solutions:SolutionForm.file_Constrain`)}
            i18nStrings={{
              uploadButtonText: (e) => (e ? "Choose files" : uploadButtonText), // multiple文件的时候，e=true，否则为false
              dropzoneText: (e) => (e ? "Drop files to upload" : "Drop file to upload"),
              removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
              limitShowFewer: "Show fewer files",
              limitShowMore: "Show more files",
              errorIconAriaLabel: "Error",
            }}
            showFileLastModified
            showFileSize
            showFileThumbnail
          />
          <ProgressBar value={gtmFiles.find((Obj) => Obj.id === item.id)?.progress || 0} label={t(`casestudies:CasestudyCreateForm.progressBarLabel`)} />
        </FormField>
      </SpaceBetween>
    </ExpandableSection>
  );
}

/*****************************************************************************/
export default function GtmPanel({ formattedData, setFormattedData, gtmFiles, setGtmFiles, setSubmitDisabled }) {
  const { contentLanguage } = useUserProfile();
  const { t } = useTranslation();

  return (
    <>
      <SpaceBetween size="l">
        {/* The 1st container: Basic Information ++++++++++++++++++++++++++++++++++++++++++++++ */}
        <Container header={<Header variant="h2">{t("solutions:SolutionForm.containerBasicInformation")}</Header>}>
          <SpaceBetween size="l">
            <FormField label={t(`solutions:SolutionForm.solutionName`)} description={t(`solutions:SolutionForm.solutionName_Des`)}>
              <Input
                value={formattedData && formattedData["solutionName_" + contentLanguage]}
                // onChange={({ detail: { value } }) => onChangeFormHandler("solutionName_en", value)}
                readOnly
              />
            </FormField>
          </SpaceBetween>
        </Container>

        {/* The 2nd container: Materials ++++++++++++++++++++++++++++++++++++++++++++++++++++++ */}
        <>
          {((formattedData && formattedData["gtmMaterials_" + contentLanguage]) || []).map((item, itemIndex) => (
            <div key={item.id}>
              <Container
                header={
                  <Header variant="h2" description={`Material ${itemIndex + 1}`}>
                    {t("solutions:SolutionForm.containerGtmMaterials")}
                  </Header>
                }
                footer={
                  <FileUploaderFooter
                    formattedData={formattedData}
                    gtmFiles={gtmFiles}
                    setGtmFiles={setGtmFiles}
                    setSubmitDisabled={setSubmitDisabled}
                    item={item}
                  />
                }
              >
                <SpaceBetween size="l">
                  <SpaceBetween size="l" direction="horizontal">
                    <FormField label={t(`solutions:SolutionForm.materialCategory`)} description={t(`solutions:SolutionForm.materialCategory_Des`)}>
                      <Select
                        selectedOption={item.category && item.category}
                        onChange={({ detail: { selectedOption } }) => {
                          DEBUG && console.log("%cselectedOption", "color: blue; font-weight: bold;", selectedOption);
                          updateForm(setFormattedData, `gtmMaterials_en`, "category", selectedOption, item.id);
                          updateForm(setFormattedData, `gtmMaterials_cn`, "category", selectedOption, item.id);
                        }}
                        options={gtmMaterialsCategoryOptions}
                        placeholder={t(`solutions:SolutionForm.materialCategory_Placeholder`)}
                        selectedAriaLabel="Selected"
                      />
                    </FormField>
                    <FormField label={t(`solutions:SolutionForm.materialName`)} description={t(`solutions:SolutionForm.materialName_Des`)}>
                      <Input
                        value={item.name || ""}
                        placeholder={t(`solutions:SolutionForm.materialName_Placeholder`)}
                        onChange={({ detail: { value } }) => {
                          DEBUG && console.log("%cprimaryIndex", "color: blue; font-weight: bold;", itemIndex);
                          DEBUG && console.log("%cvalue", "color: blue; font-weight: bold;", value);
                          updateForm(setFormattedData, `gtmMaterials_${contentLanguage}`, "name", value, item.id);
                        }}
                      />
                    </FormField>
                    <FormField label={t(`solutions:SolutionForm.materialUrl`)} description={t(`solutions:SolutionForm.materialUrl_Des`)}>
                      <Input
                        value={item.url || ""}
                        placeholder={t(`solutions:SolutionForm.materialUrl_Placeholder`)}
                        onChange={({ detail: { value } }) => {
                          DEBUG && console.log("%cprimaryIndex", "color: blue; font-weight: bold;", itemIndex);
                          DEBUG && console.log("%cvalue", "color: blue; font-weight: bold;", value);
                          updateForm(setFormattedData, `gtmMaterials_en`, "url", value, item.id);
                          updateForm(setFormattedData, `gtmMaterials_cn`, "url", value, item.id);
                        }}
                      />
                    </FormField>
                    <FormField label={t(`solutions:SolutionForm.materialDownloadable`)} description={t(`solutions:SolutionForm.materialDownloadable_Des`)}>
                      <Toggle
                        onChange={({ detail: { checked } }) => {
                          updateForm(setFormattedData, `gtmMaterials_en`, "downloadable", checked, item.id);
                          updateForm(setFormattedData, `gtmMaterials_cn`, "downloadable", checked, item.id);
                        }}
                        checked={item.downloadable || false}
                      />
                    </FormField>
                    <FormField label={t(`solutions:SolutionForm.materialUpdateDate`)} description={t(`solutions:SolutionForm.materialUpdateDate_Des`)}>
                      <DatePicker
                        placeholder="YYYY/MM/DD"
                        value={item.updateDate}
                        onChange={({ detail: { value } }) => {
                          updateForm(setFormattedData, `gtmMaterials_en`, "updateDate", value, item.id);
                          updateForm(setFormattedData, `gtmMaterials_cn`, "updateDate", value, item.id);
                        }}
                      />
                    </FormField>
                  </SpaceBetween>
                  {/* add up/down/remove buttons */}
                  <FormField label={t(`FormButton.upDownRemove`)} description={t(`FormButton.upDownRemove_Des`)}>
                    <SpaceBetween size="s" direction="horizontal">
                      <Button
                        iconName="angle-up"
                        onClick={(event) => {
                          event.preventDefault();
                          moveForm(setFormattedData, `gtmMaterials_en`, "up", itemIndex);
                          moveForm(setFormattedData, `gtmMaterials_cn`, "up", itemIndex);
                        }}
                      />
                      <Button
                        iconName="angle-down"
                        onClick={(event) => {
                          event.preventDefault();
                          moveForm(setFormattedData, `gtmMaterials_en`, "down", itemIndex);
                          moveForm(setFormattedData, `gtmMaterials_cn`, "down", itemIndex);
                        }}
                      />
                      <Button
                        onClick={(event) => {
                          event.preventDefault();
                          removeForm(setFormattedData, `gtmMaterials_en`, item.id);
                          removeForm(setFormattedData, `gtmMaterials_cn`, item.id);
                        }}
                      >
                        {t(`FormButton.removeButton`)}
                      </Button>
                    </SpaceBetween>
                  </FormField>
                </SpaceBetween>
              </Container>
            </div>
          ))}
          <Button
            onClick={(event) => {
              event.preventDefault();
              const randomId = uuidv4();
              addForm(setFormattedData, `gtmMaterials_en`, {
                category: "",
                name: "",
                url: "",
                downloadable: false,
                updateDate: "",
                fileKey: "",
                id: randomId,
              });
              addForm(setFormattedData, `gtmMaterials_cn`, {
                category: "",
                name: "",
                url: "",
                downloadable: false,
                updateDate: "",
                fileKey: "",
                id: randomId,
              });
            }}
          >
            {t(`solutions:SolutionEditForm.addGtmMaterialsButton`)}
          </Button>
        </>
      </SpaceBetween>
    </>
  );
}
